import React, { useState, useEffect, useRef } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';

const C = {
  navy: '#0D1B2A', teal: '#00B4D8', tealLight: '#E0F7FC',
  success: '#10B981', successLight: '#D1FAE5',
  blue: '#3B82F6', blueLight: '#EFF6FF',
  purple: '#8B5CF6', purpleLight: '#F5F3FF',
  orange: '#F59E0B', orangeLight: '#FEF3C7',
  grey: '#6B7280', border: '#E5E7EB', bg: '#F8FAFC',
  white: '#FFFFFF', dark: '#1F2937', red: '#EF4444',
};

const LESSON_TYPES = ['grammar', 'vocabulary', 'reading', 'writing', 'conversation', 'ielts', 'business'];
const CEFR_LEVELS  = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

const TYPE_ICONS = {
  grammar: '📚', vocabulary: '📖', reading: '📰',
  writing: '✍️', conversation: '💬', ielts: '🎯', business: '💼',
};

const LEVEL_COLORS = {
  A1: '#64748B', A2: '#0EA5E9',
  B1: '#10B981', B2: '#3B82F6',
  C1: '#8B5CF6', C2: '#F59E0B',
};

export default function SuperAdminLessonBank() {
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [triggerCount, setTriggerCount] = useState(5);
  const [triggerMsg, setTriggerMsg] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const pollRef = useRef(null);

  const fetchStats = async () => {
    try {
      const [s, h] = await Promise.all([
        api.get('/api/superadmin/lesson-bank/stats'),
        api.get('/api/superadmin/lesson-bank/history'),
      ]);
      setStats(s.data);
      // h.data may be a plain array or {history:[...]}
      const hData = Array.isArray(h.data) ? h.data : (h.data?.history || []);
      setHistory(hData);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchProgress = async () => {
    try {
      const r = await api.get('/api/superadmin/lesson-bank/progress');
      setProgress(r.data);
      if (!r.data.is_running) {
        clearInterval(pollRef.current);
        fetchStats(); // refresh stats when done
      }
    } catch (e) {}
  };

  useEffect(() => {
    fetchStats();
  }, []);

  // If running on load, start polling
  useEffect(() => {
    if (stats?.is_running) startPolling();
    return () => clearInterval(pollRef.current);
  }, [stats?.is_running]);

  const startPolling = () => {
    clearInterval(pollRef.current);
    fetchProgress();
    pollRef.current = setInterval(fetchProgress, 4000);
  };

  const triggerGeneration = async () => {
    setTriggering(true);
    setTriggerMsg('');
    try {
      const r = await api.post('/api/superadmin/lesson-bank/trigger', { count: triggerCount });
      if (r.data.started) {
        setTriggerMsg(`✅ Generation started (PID ${r.data.pid}) — generating ${triggerCount} lessons per slot across all 42 combinations`);
        setActiveTab('progress');
        startPolling();
        fetchStats();
      }
    } catch (e) {
      const msg = e.response?.data?.error || 'Failed to start';
      setTriggerMsg(`❌ ${msg}`);
    } finally {
      setTriggering(false);
    }
  };

  const totalExercises = stats ? Object.values(stats.lessons_by_type || {}).reduce((a, t) => a + (t.exercises || 0), 0) : 0;

  return (
    <Layout role="superadmin">
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 800, color: C.navy }}>📚 Lesson Bank</h2>
        <p style={{ margin: 0, color: C.grey, fontSize: 14 }}>
          Content database — AI generates once a month, students learn from it forever
        </p>
      </div>

      {/* Status bar */}
      {stats?.is_running && (
        <div style={{ background: '#FEF9C3', border: '1px solid #FDE68A', borderRadius: 10, padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: C.orange, animation: 'pulse 1.5s infinite' }} />
          <strong style={{ color: '#92400E' }}>Generation in progress</strong>
          <span style={{ color: '#92400E', fontSize: 13 }}>— adding new lessons to the bank. This takes 10–15 minutes.</span>
          <button onClick={() => setActiveTab('progress')} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: C.orange, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
            View Progress →
          </button>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: `1px solid ${C.border}`, paddingBottom: 0 }}>
        {[
          { key: 'overview', label: '📊 Overview' },
          { key: 'breakdown', label: '📋 Full Breakdown' },
          { key: 'topup', label: '⚡ Top Up' },
          { key: 'progress', label: `🔄 Progress${stats?.is_running ? ' •' : ''}` },
          { key: 'history', label: '📅 History' },
        ].map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
            padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer',
            fontWeight: activeTab === t.key ? 700 : 400,
            color: activeTab === t.key ? C.teal : C.grey,
            borderBottom: activeTab === t.key ? `2px solid ${C.teal}` : '2px solid transparent',
            fontSize: 14, marginBottom: -1,
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: C.grey }}>Loading lesson bank data…</div>
      ) : (
        <>
          {/* ── OVERVIEW TAB ── */}
          {activeTab === 'overview' && stats && (
            <div>
              {/* Top stats */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
                {[
                  { label: 'Total Lessons', value: stats.total_lessons, icon: '📚', color: C.teal },
                  { label: 'Total Exercises', value: totalExercises.toLocaleString(), icon: '✏️', color: C.blue },
                  { label: 'Lesson Types', value: 7, icon: '🗂️', color: C.purple },
                  { label: 'CEFR Levels', value: 6, icon: '📊', color: C.success },
                  { label: 'Last Run Cost', value: stats.last_run_cost ? `$${stats.last_run_cost}` : '—', icon: '💰', color: C.orange },
                  { label: 'Added Last Run', value: stats.last_run_lessons || '—', icon: '➕', color: C.success },
                ].map(s => (
                  <div key={s.label} style={{ background: C.white, borderRadius: 12, padding: '16px 18px', border: `1px solid ${C.border}`, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                    <div style={{ fontSize: 22, marginBottom: 6 }}>{s.icon}</div>
                    <div style={{ fontSize: 24, fontWeight: 800, color: s.color }}>{s.value}</div>
                    <div style={{ fontSize: 12, color: C.grey, marginTop: 2 }}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Last run info */}
              <div style={{ background: C.white, borderRadius: 12, padding: '18px 20px', border: `1px solid ${C.border}`, marginBottom: 20 }}>
                <h4 style={{ margin: '0 0 12px', color: C.navy, fontSize: 15, fontWeight: 700 }}>⏱️ Last Generation Run</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 12, color: C.grey, marginBottom: 2 }}>Date & Time</div>
                    <div style={{ fontWeight: 700, color: C.dark, fontSize: 14 }}>{stats.last_run_at || 'No runs yet'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: C.grey, marginBottom: 2 }}>Lessons Generated</div>
                    <div style={{ fontWeight: 700, color: C.success, fontSize: 14 }}>{stats.last_run_lessons ? `+${stats.last_run_lessons}` : '—'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: C.grey, marginBottom: 2 }}>API Cost</div>
                    <div style={{ fontWeight: 700, color: C.orange, fontSize: 14 }}>{stats.last_run_cost ? `$${stats.last_run_cost}` : '—'}</div>
                  </div>
                </div>
              </div>

              {/* Lessons by type — bar chart */}
              <div style={{ background: C.white, borderRadius: 12, padding: '18px 20px', border: `1px solid ${C.border}` }}>
                <h4 style={{ margin: '0 0 16px', color: C.navy, fontSize: 15, fontWeight: 700 }}>📊 Lessons by Type</h4>
                {LESSON_TYPES.map(type => {
                  const d = stats.lessons_by_type?.[type] || { count: 0, exercises: 0 };
                  const max = Math.max(...LESSON_TYPES.map(t => stats.lessons_by_type?.[t]?.count || 0));
                  const pct = max ? (d.count / max) * 100 : 0;
                  return (
                    <div key={type} style={{ marginBottom: 14 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 13 }}>
                        <span>{TYPE_ICONS[type]} <strong>{type.charAt(0).toUpperCase() + type.slice(1)}</strong></span>
                        <span style={{ color: C.grey }}>{d.count} lessons · {d.exercises} exercises</span>
                      </div>
                      <div style={{ height: 8, background: '#F1F5F9', borderRadius: 4 }}>
                        <div style={{ height: '100%', borderRadius: 4, background: C.teal, width: `${pct}%`, transition: 'width 0.5s ease' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── BREAKDOWN TAB ── */}
          {activeTab === 'breakdown' && stats && (
            <div style={{ background: C.white, borderRadius: 12, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: C.bg }}>
                    <th style={th}>Type</th>
                    {CEFR_LEVELS.map(l => <th key={l} style={{ ...th, color: LEVEL_COLORS[l] }}>{l}</th>)}
                    <th style={th}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {LESSON_TYPES.map(type => {
                    const row = stats.lessons_by_type_level?.[type] || {};
                    const total = CEFR_LEVELS.reduce((a, l) => a + (row[l] || 0), 0);
                    return (
                      <tr key={type} style={{ borderTop: `1px solid ${C.border}` }}>
                        <td style={td}><strong>{TYPE_ICONS[type]} {type.charAt(0).toUpperCase() + type.slice(1)}</strong></td>
                        {CEFR_LEVELS.map(l => (
                          <td key={l} style={{ ...td, textAlign: 'center' }}>
                            <span style={{
                              display: 'inline-block', minWidth: 28, padding: '2px 8px',
                              borderRadius: 6, fontSize: 13, fontWeight: 700,
                              background: (row[l] || 0) >= 10 ? '#D1FAE5' : (row[l] || 0) >= 5 ? '#FEF9C3' : '#FEE2E2',
                              color: (row[l] || 0) >= 10 ? '#065F46' : (row[l] || 0) >= 5 ? '#92400E' : '#991B1B',
                            }}>
                              {row[l] || 0}
                            </span>
                          </td>
                        ))}
                        <td style={{ ...td, textAlign: 'center', fontWeight: 700, color: C.navy }}>{total}</td>
                      </tr>
                    );
                  })}
                  <tr style={{ borderTop: `2px solid ${C.border}`, background: C.bg, fontWeight: 700 }}>
                    <td style={td}><strong>Total</strong></td>
                    {CEFR_LEVELS.map(l => {
                      const sum = LESSON_TYPES.reduce((a, t) => a + (stats.lessons_by_type_level?.[t]?.[l] || 0), 0);
                      return <td key={l} style={{ ...td, textAlign: 'center' }}>{sum}</td>;
                    })}
                    <td style={{ ...td, textAlign: 'center', color: C.teal, fontSize: 16 }}>{stats.total_lessons}</td>
                  </tr>
                </tbody>
              </table>
              <div style={{ padding: '10px 16px', fontSize: 12, color: C.grey, borderTop: `1px solid ${C.border}` }}>
                🟢 10+ lessons &nbsp;&nbsp; 🟡 5–9 lessons &nbsp;&nbsp; 🔴 Under 5 lessons — consider a top-up
              </div>
            </div>
          )}

          {/* ── TOP UP TAB ── */}
          {activeTab === 'topup' && (
            <div style={{ maxWidth: 560 }}>
              <div style={{ background: C.white, borderRadius: 12, padding: '24px', border: `1px solid ${C.border}`, marginBottom: 20 }}>
                <h3 style={{ margin: '0 0 6px', color: C.navy, fontWeight: 800 }}>⚡ Trigger Lesson Bank Top-Up</h3>
                <p style={{ margin: '0 0 20px', color: C.grey, fontSize: 14 }}>
                  Generates new lessons across all 7 types × 6 CEFR levels (42 combinations) using Claude Haiku.
                  Runs in the background — takes 10–15 minutes.
                </p>

                <label style={{ display: 'block', fontWeight: 700, fontSize: 14, color: C.dark, marginBottom: 10 }}>
                  How many new lessons per slot?
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 20 }}>
                  {[
                    { count: 2,  lessons: 84,  cost: '~$0.15', label: 'Weekly default' },
                    { count: 5,  lessons: 210, cost: '~$0.35', label: 'Recommended' },
                    { count: 10, lessons: 420, cost: '~$0.70', label: 'Big top-up' },
                  ].map(opt => (
                    <button key={opt.count} onClick={() => setTriggerCount(opt.count)}
                      style={{
                        padding: '14px 12px', borderRadius: 10, cursor: 'pointer', textAlign: 'center',
                        border: triggerCount === opt.count ? `2px solid ${C.teal}` : `1px solid ${C.border}`,
                        background: triggerCount === opt.count ? C.tealLight : C.white,
                        transition: 'all 0.15s',
                      }}>
                      <div style={{ fontSize: 22, fontWeight: 900, color: triggerCount === opt.count ? C.teal : C.navy }}>{opt.count}</div>
                      <div style={{ fontSize: 11, color: C.grey }}>+{opt.lessons} lessons</div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: triggerCount === opt.count ? C.teal : C.grey }}>{opt.cost}</div>
                      <div style={{ fontSize: 10, color: C.grey, marginTop: 2 }}>{opt.label}</div>
                    </button>
                  ))}
                </div>

                <div style={{ background: C.bg, borderRadius: 8, padding: '10px 14px', marginBottom: 20, fontSize: 13, color: C.grey }}>
                  <strong style={{ color: C.dark }}>{triggerCount} lessons × 42 slots</strong> = <strong style={{ color: C.teal }}>{triggerCount * 42} new lessons</strong> added to the bank
                </div>

                {triggerMsg && (
                  <div style={{
                    padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13,
                    background: triggerMsg.startsWith('✅') ? C.successLight : '#FEE2E2',
                    color: triggerMsg.startsWith('✅') ? '#065F46' : '#991B1B',
                    border: `1px solid ${triggerMsg.startsWith('✅') ? '#6EE7B7' : '#FECACA'}`,
                  }}>
                    {triggerMsg}
                  </div>
                )}

                <button onClick={triggerGeneration}
                  disabled={triggering || stats?.is_running}
                  style={{
                    width: '100%', padding: '14px', borderRadius: 10, border: 'none',
                    background: triggering || stats?.is_running ? C.border : C.teal,
                    color: triggering || stats?.is_running ? C.grey : C.white,
                    fontWeight: 700, fontSize: 15, cursor: triggering || stats?.is_running ? 'not-allowed' : 'pointer',
                  }}>
                  {stats?.is_running ? '⏳ Generation already running…' : triggering ? 'Starting…' : `⚡ Generate +${triggerCount * 42} Lessons Now`}
                </button>
              </div>

              <div style={{ background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 10, padding: '14px 16px', fontSize: 13, color: '#92400E' }}>
                <strong>ℹ️ Auto schedule:</strong> The weekly cron job runs every Sunday at 2:00 AM UTC and adds 2 lessons per slot automatically. You only need to manually trigger for larger top-ups.
              </div>
            </div>
          )}

          {/* ── PROGRESS TAB ── */}
          {activeTab === 'progress' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ margin: 0, color: C.navy, fontWeight: 800 }}>🔄 Generation Progress</h3>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  {progress?.is_running && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: C.orange }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: C.orange, animation: 'blink 1s infinite' }} />
                      Running
                    </div>
                  )}
                  {progress?.completed && !progress?.is_running && (
                    <div style={{ fontSize: 13, color: C.success, fontWeight: 700 }}>✅ Completed</div>
                  )}
                  <button onClick={() => { fetchProgress(); fetchStats(); }} style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.white, cursor: 'pointer', fontSize: 13 }}>
                    Refresh
                  </button>
                </div>
              </div>
              <div style={{
                background: '#0D1B2A', borderRadius: 12, padding: '16px 20px',
                fontFamily: 'monospace', fontSize: 13, color: '#A3E4D7',
                minHeight: 300, maxHeight: 500, overflowY: 'auto',
                lineHeight: 1.7,
              }}>
                {progress?.lines?.length ? (
                  progress.lines.map((line, i) => (
                    <div key={i} style={{
                      color: line.includes('COMPLETE') ? '#6EE7B7' :
                             line.includes('ERROR') ? '#FCA5A5' :
                             line.includes('Cost:') ? '#FCD34D' :
                             line.includes('Stored') ? '#86EFAC' : '#A3E4D7'
                    }}>
                      {line}
                    </div>
                  ))
                ) : (
                  <div style={{ color: '#64748B' }}>
                    {(progress?.is_running || stats?.is_running) ? 'Generation started — log updating every 4 seconds…' : 'No generation currently running. Use the Top Up tab to start one.'}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── HISTORY TAB ── */}
          {activeTab === 'history' && (
            <div style={{ background: C.white, borderRadius: 12, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
              {history.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: C.grey }}>No generation history found.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: C.bg }}>
                      <th style={th}>Date / Time</th>
                      <th style={th}>Type</th>
                      <th style={th}>Lessons Added</th>
                      <th style={th}>API Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((h, i) => (
                      <tr key={i} style={{ borderTop: `1px solid ${C.border}` }}>
                        <td style={td}>{h.time || '—'}</td>
                        <td style={td}>
                          <span style={{
                            padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                            background: h.type === 'initial' ? C.purpleLight : h.type === 'topup' ? C.tealLight : C.blueLight,
                            color: h.type === 'initial' ? C.purple : h.type === 'topup' ? C.teal : C.blue,
                          }}>
                            {h.type === 'initial' ? '🚀 Initial' : h.type === 'topup' ? '⚡ Top-Up' : '🔄 Weekly'}
                          </span>
                        </td>
                        <td style={{ ...td, fontWeight: 700, color: C.success }}>+{h.lessons_added}</td>
                        <td style={{ ...td, color: C.orange }}>${parseFloat(h.cost || 0).toFixed(4)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: `2px solid ${C.border}`, background: C.bg }}>
                      <td style={td} colSpan={2}><strong>Total</strong></td>
                      <td style={{ ...td, fontWeight: 700, color: C.success }}>
                        +{history.reduce((a, h) => a + (parseInt(h.lessons_added) || 0), 0)}
                      </td>
                      <td style={{ ...td, color: C.orange, fontWeight: 700 }}>
                        ${history.reduce((a, h) => a + parseFloat(h.cost || 0), 0).toFixed(4)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          )}
        </>
      )}
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.5}} @keyframes blink{0%,100%{opacity:1}50%{opacity:0.3}}`}</style>
    </Layout>
  );
}

const th = { padding: '10px 16px', textAlign: 'left', fontSize: 13, fontWeight: 700, color: '#6B7280' };
const td = { padding: '12px 16px', fontSize: 13, color: '#1F2937' };
